import axios from 'axios';
import api from './api';
import { sessionHeaders } from '../utils/session';

// Trailing slash stripped: every call below appends "/api/..." by hand, and a
// base of "https://api.teamlio.ai/" would produce "//api/...", which FastAPI 404s.
const TEAMLIO_AUTH_URL = (
  process.env.REACT_APP_TEAMLIO_AUTH_URL || 'http://localhost:8000'
).replace(/\/+$/, '');

export interface Organization {
  head_office_id: string;
  company_name: string;
  organization: string;
}

export interface GetOrganizationsResult {
  success: boolean;
  organizations?: Organization[];
}

export interface LoginPayload {
  email: string;
  password: string;
  head_office_id: string;
}

export interface LoginResult {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string | null;
    [k: string]: unknown;
  };
}

export async function getOrganizations(email: string): Promise<GetOrganizationsResult> {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/get-organizations`,
    { email },
    { timeout: 15000 }
  );
  return data;
}

export async function login(payload: LoginPayload): Promise<LoginResult> {
  // TeamLio's /api/auth/login-select accepts JSON with email, password, head_office_id.
  // The X-Client-* headers are what let the backend claim this platform's
  // single-session slot; without them the login is treated as unidentified
  // and claims nothing.
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/login-select`,
    {
      email: payload.email,
      password: payload.password,
      head_office_id: payload.head_office_id,
    },
    { timeout: 15000, headers: await sessionHeaders() }
  );
  if (!data?.access_token) {
    throw new Error('No access token in login response');
  }
  return data;
}

export interface GoogleAuthResult {
  success: boolean;
  email: string;
  ticket: string;
  organizations?: Organization[];
}

// Sign in with Google replaces the PASSWORD, not the workspace step: one email
// can belong to several workspaces, so this returns the same list the email
// step returns, plus a short-lived ticket that stands in for the password.
export async function googleAuth(credential: string): Promise<GoogleAuthResult> {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/google`,
    { credential },
    { timeout: 15000 }
  );
  return data;
}

export async function googleLogin(
  ticket: string,
  head_office_id: string
): Promise<LoginResult> {
  // Same X-Client-* headers as login(): without them this login claims no
  // single-session slot for the messenger surface.
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/google/login-select`,
    { ticket, head_office_id },
    { timeout: 15000, headers: await sessionHeaders() }
  );
  if (!data?.access_token) {
    throw new Error('No access token in login response');
  }
  return data;
}

export async function refresh(refreshToken: string): Promise<LoginResult> {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/refresh`,
    { refresh_token: refreshToken },
    { timeout: 15000 }
  );
  return data;
}

// Who is this Google account, for the signup form. Unlike googleAuth() this
// does not refuse an email with no account — that is the point of a signup.
// The ticket it returns stands in for the password.
export async function googleSignupIdentity(credential: string) {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/google/signup`,
    { credential },
    { timeout: 15000 }
  );
  return data;
}

// Mail a 6-digit code to the address a signup is being started on. Refuses
// (409) an address that already owns a workspace, and (429) a second request
// inside the resend cooldown.
export async function sendRegistrationOtp(email: string) {
  try {
    const { data } = await api.post(`${TEAMLIO_AUTH_URL}/api/auth/register/send-otp`, { email });
    return data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.detail || error.message || "Could not send verification code",
    );
  }
}

// Spend the signup code. The backend then lets register() through for this
// address for a short while, so this must succeed before register().
export async function verifyRegistrationOtp(email: string, otp: string) {
  try {
    const { data } = await api.post(`${TEAMLIO_AUTH_URL}/api/auth/register/verify-otp`, { email, otp });
    return data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.detail || error.message || "Invalid or expired code",
    );
  }
}

export const register = async (
  email: string,
  password: string,
  role: string,
  first_name: string,
  last_name: string,
  // Required by the backend (UserCreate wants 10-15 digits). It used to be
  // missing from this form altogether, so every registration from the
  // messenger came back 422.
  mobile: string,
  company_name?: string,
  google_ticket?: string,
) => {
  try {
    console.log("auth.ts: Making registration request for:", email, "with role:", role);
    const payload: Record<string, any> = {
      email,
      password,
      role,
      first_name,
      last_name,
      mobile,
    };
    // A Google signup has no password: the ticket proves the address instead.
    if (google_ticket) {
      payload.google_ticket = google_ticket;
      delete payload.password;
    }
    const trimmedCompany = (company_name || "").trim();
    if (trimmedCompany) payload.company_name = trimmedCompany;

    const response = await api.post(`${TEAMLIO_AUTH_URL}/api/auth/register`, payload);
    console.log("auth.ts: Registration response data:", response.data);
    return response.data;
  } catch (error: any) {
    console.error('auth.ts: Registration error:', error);
    console.error('auth.ts: Error response:', error?.response?.data);
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export async function logout(): Promise<void> {
  // Release this device's single-session slot BEFORE clearing the stored
  // token below. /api/auth/logout needs the bearer token (attached by
  // api.ts's request interceptor) to know whose slot to free, and it writes
  // the audit `logout` row to session_events — once localStorage is cleared
  // there is no token left to authenticate the request with. This exact
  // ordering bug has already shipped once (this function used to only clear
  // storage and never called the endpoint at all) — do NOT move the network
  // call after the removeItem calls a second time. A failure here must never
  // block local sign-out.
  try {
    await api.post(`${TEAMLIO_AUTH_URL}/api/auth/logout`);
  } catch (error) {
    console.warn('auth.ts: logout slot release failed (signing out anyway)', error);
  }

  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } catch {
    /* ignore */
  }
}
